/**
 * アプリ全体の UI 状態 (Zustand)。
 *
 * フェーズ遷移:
 *   idle → validating → processing → done (→ idle で reset)
 *                     → error (任意で idle へ戻せる)
 */

import { create } from 'zustand';

import type { VehicleMetadata } from '@shared/types';

export type AppPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'validating' }
  | {
      readonly kind: 'processing';
      readonly step: ProcessingStep;
      readonly current: number;
      readonly total: number;
    }
  | {
      readonly kind: 'done';
      readonly zipBlob: Blob;
      readonly zipName: string;
      readonly summary: DoneSummary;
    }
  | { readonly kind: 'error'; readonly message: string };

export type ProcessingStep =
  | 'classifying'
  | 'processing-images'
  | 'removing-reflections'
  | 'composing-hero'
  | 'building-zip';

export interface DoneSummary {
  readonly totalPhotos: number;
  readonly heroFallbackUsed: boolean;
  /** 「福山工業所」文字を除去できた枚数 */
  readonly cleanedCount: number;
  readonly elapsedMs: number;
}

export interface MetadataInput {
  readonly modelInput: string;
  readonly yearInput: string;
  readonly gradeInput: string;
  readonly mileageInput: string;
  readonly equipmentText: string;
  readonly appeal1: string;
  readonly appeal2: string;
  readonly appeal3: string;
}

export const EMPTY_METADATA: MetadataInput = {
  modelInput: '',
  yearInput: '',
  gradeInput: '',
  mileageInput: '',
  equipmentText: '',
  appeal1: '',
  appeal2: '',
  appeal3: '',
};

export interface AppState {
  readonly files: readonly File[];
  readonly heroIndex: number;
  readonly metadata: MetadataInput;
  readonly phase: AppPhase;
  readonly errors: readonly string[];
  setFiles: (files: readonly File[]) => void;
  /**
   * Wave 22: 既存ファイルに追加する (上書きしない)。
   * - 重複 (同じ name + size) は除外
   * - 上限 80 枚を超える分は切り捨て
   * - 1 枚目 (hero) を維持するため heroIndex は変えない
   */
  addFiles: (files: readonly File[]) => void;
  /** Wave 22: 1 枚個別削除 */
  removeFileAt: (index: number) => void;
  /** Wave 22: 全クリア (ファイル + heroIndex リセット) */
  clearFiles: () => void;
  setHeroIndex: (index: number) => void;
  setMetadata: (patch: Partial<MetadataInput>) => void;
  setPhase: (phase: AppPhase) => void;
  setErrors: (errors: readonly string[]) => void;
  reset: () => void;
}

const INITIAL_STATE: Pick<AppState, 'files' | 'heroIndex' | 'metadata' | 'phase' | 'errors'> = {
  files: [],
  heroIndex: 0,
  metadata: EMPTY_METADATA,
  phase: { kind: 'idle' },
  errors: [],
};

const MAX_PHOTOS = 80;

/** 重複判定キー (同じ name + size を同一ファイルとみなす) */
function fileKey(file: File): string {
  return `${file.name}__${file.size}`;
}

export const useAppStore = create<AppState>((set) => ({
  ...INITIAL_STATE,
  setFiles: (files) => set({ files, heroIndex: 0 }),
  addFiles: (newFiles) =>
    set((state) => {
      const existingKeys = new Set(state.files.map(fileKey));
      const appendable: File[] = [];
      for (const f of newFiles) {
        if (existingKeys.has(fileKey(f))) continue; // 重複スキップ
        appendable.push(f);
        existingKeys.add(fileKey(f));
      }
      const merged = [...state.files, ...appendable].slice(0, MAX_PHOTOS);
      return { files: merged };
    }),
  removeFileAt: (index) =>
    set((state) => {
      if (index < 0 || index >= state.files.length) return state;
      const next = state.files.filter((_, i) => i !== index);
      // heroIndex 調整: 削除されたのが hero 以前なら -1、hero 自身なら 0 にリセット
      let nextHero = state.heroIndex;
      if (next.length === 0) nextHero = 0;
      else if (index < state.heroIndex) nextHero = state.heroIndex - 1;
      else if (index === state.heroIndex) nextHero = 0;
      return { files: next, heroIndex: nextHero };
    }),
  clearFiles: () => set({ files: [], heroIndex: 0 }),
  setHeroIndex: (index) => set({ heroIndex: index }),
  setMetadata: (patch) => set((state) => ({ metadata: { ...state.metadata, ...patch } })),
  setPhase: (phase) => set({ phase }),
  setErrors: (errors) => set({ errors }),
  reset: () => set({ ...INITIAL_STATE }),
}));

/**
 * MetadataInput (文字列フォーム) → VehicleMetadata (型付き)。
 * バリデーションは呼び出し側で実施。ここは変換のみ。
 */
export function toVehicleMetadata(input: MetadataInput): VehicleMetadata {
  return {
    model: input.modelInput.trim(),
    year: Number.parseInt(input.yearInput, 10) || 0,
    grade: input.gradeInput.trim(),
    mileage: Number.parseInt(input.mileageInput.replace(/[,、\s]/g, ''), 10) || 0,
    equipment: input.equipmentText
      .split(/[,、\n]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0),
    appeals: [input.appeal1.trim(), input.appeal2.trim(), input.appeal3.trim()],
  };
}
